// fuzzCore.js — the sound of fuzz:{...}: a fuzz pedal on a dying battery
// -----------------------------------------------
// Pure, so it can be tested without an AudioContext; fuzzWorklet.js runs
// it inside the audio engine, fuzz.js puts it in a synth's voice.
//
// A transistor fuzz whose supply is starved: the rails sit low and sag
// further under load, the operating point drifts off centre, the signal
// near zero falls into a dead zone (the splatty, gated buzz), and the
// failing supply wobbles chaotically (a logistic map) and drops out in
// short crackles. A DC blocker and two gentle lowpasses keep it warm.

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const num = (v, d) => (Number.isFinite(Number(v)) ? Number(v) : d);

/**
 * The settings of fuzz: -- a number is how starved (fuzz:0.7), an object
 * the lot: { starve 0..1 (0.5), gain 1..200 (30), bias 0..1 (0.3),
 * tone Hz (2500) }.
 */
export function fuzzSettings(cfg) {
  const c = typeof cfg === "object" && cfg ? cfg : { starve: cfg };
  return {
    starve: clamp(num(c.starve ?? c.amount, 0.5), 0, 1),
    gain: clamp(num(c.gain, 30), 1, 200),
    bias: clamp(num(c.bias, 0.3), 0, 1),
    tone: clamp(num(c.tone, 2500), 200, 16000),
  };
}

export class FuzzCore {
  constructor(sampleRate, settings = {}, random = Math.random) {
    this.sr = sampleRate;
    this.random = random;
    this.set(settings);
    this.sag = 0;              // the supply's load: follows the output's power
    this.chaos = 0.4;          // the logistic map's state
    this.wobble = 0.4;         // ... smoothed
    this.n = 0;
    this.drop = 0;             // samples left of a dropout
    this.dcx = 0; this.dcy = 0;
    this.lp1 = 0; this.lp2 = 0;
  }

  set(settings) {
    this.s = fuzzSettings(settings);
    const sr = this.sr;
    this.aSag = Math.exp(-1 / (0.15 * sr));                 // the supply recovers over ~150 ms
    this.aWob = Math.exp(-1 / (0.005 * sr));
    this.aTone = 1 - Math.exp(-2 * Math.PI * this.s.tone / sr);
    // a starved pedal is quieter: made up, partly, so the level stays usable
    this.makeup = 0.9 / Math.max(0.35, 1 - this.s.starve * 0.7);
  }

  /** One block: input -> output (Float32Arrays of the same length). */
  process(input, output) {
    const { starve, gain, bias } = this.s;
    for (let i = 0; i < input.length; i++) {
      // the failing supply: a chaotic wobble, a few hundred times a second
      if ((this.n++ & 31) === 0) this.chaos = 3.97 * this.chaos * (1 - this.chaos);
      this.wobble = this.aWob * this.wobble + (1 - this.aWob) * this.chaos;
      const volt = clamp(1 - starve * 0.8 - starve * 0.35 * this.sag + starve * 0.3 * (this.wobble - 0.5), 0.03, 1);

      // the operating point drifts off centre as the voltage falls
      const b = bias * 0.5 + starve * 0.4 * (1 - volt);
      let y = input[i] * gain + b * gain * 0.1;

      // starved, the transistor will not conduct near zero: a dead zone
      const dz = starve * gain * 0.25 * (0.6 + 0.8 * this.wobble);
      y = Math.abs(y) <= dz ? 0 : y - Math.sign(y) * dz;

      // clipping against low, uneven rails
      const rp = volt, rn = volt * (1 - 0.6 * bias);
      y = y > 0 ? rp * Math.tanh(y / rp) : -rn * Math.tanh(-y / rn);

      // crackle: the supply drops out now and then, more the more starved
      if (this.drop > 0) { this.drop--; y *= 0.05; }
      else if (this.random() < starve * starve * starve * 0.0006) this.drop = Math.round((0.0002 + this.random() * 0.004) * this.sr);

      this.sag = this.aSag * this.sag + (1 - this.aSag) * y * y;

      // no DC (the bias), then warm
      const dc = y - this.dcx + 0.995 * this.dcy;
      this.dcx = y; this.dcy = dc;
      this.lp1 += this.aTone * (dc - this.lp1);
      this.lp2 += this.aTone * (this.lp1 - this.lp2);
      output[i] = this.lp2 * this.makeup;
    }
  }
}
